import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  RangoSalarialPerfil,
  CreateRangoSalarialPerfilRequest,
  UpdateRangoSalarialPerfilRequest
} from '../models/rango-salarial-perfil.interface';
import { environment } from '../../../../environments/environment';
import { ApiEnvelope } from '../../../shared/models/api-envelope';
import { RangoSalarialPerfilMapper } from '../models/rango-salarial-perfil.mapper';
import { RangoSalarialPerfilDto } from '../models/rango-salarial-perfil.dto';

@Injectable({
  providedIn: 'root'
})
export class RangoSalarialPerfilService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl.generic}/RangoSalarialPerfil`;

  private unwrapResponse<T>() {
    return map((response: T | ApiEnvelope<T>) => {
      const maybe = response as ApiEnvelope<T>;
      return maybe && (maybe as { data?: T }).data !== undefined
        ? (maybe.data as T)
        : (response as T);
    });
  }

  private normalizeRangoSalarial(raw: RangoSalarialPerfilDto): RangoSalarialPerfil {
    return RangoSalarialPerfilMapper.fromDto(raw);
  }

  private normalizeList(list: RangoSalarialPerfilDto[]): RangoSalarialPerfil[] {
    return RangoSalarialPerfilMapper.fromDtoList(list);
  }

  /**
   * Get all salary ranges with profile names
   */
  getRangosSalariales(): Observable<RangoSalarialPerfil[]> {
    return this.http
      .get<ApiEnvelope<RangoSalarialPerfilDto[]>>(`${this.baseUrl}/GetAllWithPerfil`)
      .pipe(
        this.unwrapResponse<RangoSalarialPerfilDto[]>(),
        map((res: RangoSalarialPerfilDto[]) => this.normalizeList(res))
      );
  }

  /**
   * Get a salary range by ID
   * @param id - Salary range ID
   * @param includeNombrePerfil - Include profile name in response
   */
  getRangoSalarialById(
    id: number,
    includeNombrePerfil: boolean = true
  ): Observable<RangoSalarialPerfil> {
    const params = new HttpParams()
      .set('id', id.toString())
      .set('includeNombrePerfil', includeNombrePerfil.toString());

    return this.http
      .get<ApiEnvelope<RangoSalarialPerfilDto>>(`${this.baseUrl}/GetByID`, { params })
      .pipe(
        this.unwrapResponse<RangoSalarialPerfilDto>(),
        map((res: RangoSalarialPerfilDto) => this.normalizeRangoSalarial(res))
      );
  }

  /**
   * Create a new salary range
   * @param rangoSalarial - Salary range data
   */
  createRangoSalarial(rangoSalarial: CreateRangoSalarialPerfilRequest): Observable<{ id: number }> {
    const dto = RangoSalarialPerfilMapper.toCreateDto(
      rangoSalarial.perfilId,
      rangoSalarial.ano,
      rangoSalarial.minimo,
      rangoSalarial.maximo
    );

    return this.http
      .post<ApiEnvelope<{ id: number }>>(`${this.baseUrl}/Create`, dto)
      .pipe(this.unwrapResponse<{ id: number }>());
  }

  /**
   * Update an existing salary range
   * @param id - Salary range ID
   * @param rangoSalarial - Updated salary range data
   */
  updateRangoSalarial(
    id: number,
    rangoSalarial: UpdateRangoSalarialPerfilRequest
  ): Observable<boolean> {
    const dto = RangoSalarialPerfilMapper.toUpdateDto(
      id,
      rangoSalarial.perfilId,
      rangoSalarial.ano,
      rangoSalarial.minimo,
      rangoSalarial.maximo
    );

    return this.http.put<ApiEnvelope<boolean>>(`${this.baseUrl}/Update`, dto).pipe(
      this.unwrapResponse<boolean>(),
      map(() => true)
    );
  }
}
